import React, { useMemo, useState } from 'react';
import { intersection, isEmpty } from 'lodash';

import { Heading } from '~/components/core';
import Button from '~/components/core/Atomic/Buttons/Button';
import PlusIcon from '~/components/icons/PlusIcon';
import LoadingIndicator from '~/components/LoadingIndicator';
import useDataFetcher from '~/components/useDataFetcher';
import type {
  GuardrailRuleCreateParams,
  GuardrailRuleInfo,
  GuardrailSupportedEventsResponse,
} from '~/server_shared/openapi/data-contracts';

import CardDialog from '../../../CardDialog';
import { useSysconfig } from '../../SystemConfigurationScreen';

import {
  createGuardrailRule,
  deleteGuardrailRule,
  setGuardrailRuleEnabled,
  updateGuardrailRule,
} from './api/guardrailRulesApi';
import { GuardrailRulesFilters } from './components/GuardrailRulesFilters';
import { GuardrailRulesTable } from './components/GuardrailRulesTable';
import AddOrEditGuardrailRuleDialog from './AddOrEditGuardrailRuleDialog';
import type { GuardrailFilterParams, SysconfigOrganization } from './types';

const Guardrails: React.FC = () => {
  const { organization } = useSysconfig() as { organization: SysconfigOrganization };
  const [isProcessing, setIsProcessing] = useState(false);
  const [isRuleDialogOpen, setIsRuleDialogOpen] = useState(false);
  const [ruleToEdit, setRuleToEdit] = useState<GuardrailRuleInfo | null>(null);
  const [filteredParams, setFilteredParams] = useState<GuardrailFilterParams>({
    searchTerm: '',
    subOrgIds: [],
    lobs: [],
    outcome: 'all',
  });

  const {
    isLoading: isLoadingRules,
    isError: isErrorRules,
    data: rules,
    reloadData: reloadRules,
  } = useDataFetcher<GuardrailRuleInfo[]>(`/api/v1/organizations/${organization.id}/guardrails/rules`);

  const {
    isLoading: isLoadingEvents,
    isError: isErrorEvents,
    data: supportedEventsResponse,
  } = useDataFetcher<GuardrailSupportedEventsResponse>(
    `/api/v1/organizations/${organization.id}/guardrails/supported_events`
  );

  const supportedEvents = supportedEventsResponse?.events;
  const isLoading = isLoadingRules || isLoadingEvents;
  const isError = isErrorRules || isErrorEvents;

  const filteredRules = useMemo(() => {
    let result = rules ?? [];
    if (!rules) {
      return result;
    }
    if (!isEmpty(filteredParams.subOrgIds)) {
      result = result.filter(
        (rule) =>
          rule.scope?.is_all_sub_orgs ||
          !isEmpty(intersection(rule.scope?.sub_organization_ids ?? [], filteredParams.subOrgIds))
      );
    }
    if (!isEmpty(filteredParams.lobs)) {
      result = result.filter((rule) => {
        const ruleLobs = rule.scope?.lobs ?? [];
        return (
          rule.scope?.is_all_lobs || ruleLobs.length === 0 || intersection(filteredParams.lobs, ruleLobs).length > 0
        );
      });
    }
    if (filteredParams.outcome !== 'all') {
      result = result.filter((rule) => rule.outcome === filteredParams.outcome);
    }
    if (filteredParams.searchTerm) {
      const term = filteredParams.searchTerm.toLowerCase();
      result = result.filter(
        (rule) => (rule.name ?? '').toLowerCase().includes(term) || (rule.message ?? '').toLowerCase().includes(term)
      );
    }
    return result;
  }, [rules, filteredParams]);

  const handleCloseRuleDialog = () => {
    setIsRuleDialogOpen(false);
    setRuleToEdit(null);
  };

  const withProcessing = async (fn: () => Promise<void>) => {
    setIsProcessing(true);
    try {
      await fn();
      await reloadRules();
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSubmitRule = async (values: GuardrailRuleCreateParams) => {
    await withProcessing(async () => {
      if (ruleToEdit) {
        await updateGuardrailRule(organization.id, ruleToEdit.id, values);
      } else {
        await createGuardrailRule(organization.id, values);
      }
    });
  };

  // Delete/enable are invoked fire-and-forget from the table (ThreeDotsMenu / Switch),
  // so swallow after the service has already reported the error — otherwise the
  // rethrow from `withProcessing` becomes an unhandled promise rejection. (The
  // create/update path keeps rethrowing so the dialog's Formik catch can react.)
  const handleDeleteRule = async (rule: GuardrailRuleInfo) => {
    try {
      await withProcessing(() => deleteGuardrailRule(organization.id, rule.id));
    } catch {
      /* already surfaced via reportAxiosError */
    }
  };

  const handleEnabledChanged = async (rule: GuardrailRuleInfo) => {
    try {
      await withProcessing(() => setGuardrailRuleEnabled(organization.id, rule.id, !rule.is_enabled));
    } catch {
      /* already surfaced via reportAxiosError */
    }
  };

  const handleEditRuleClicked = (rule: GuardrailRuleInfo) => {
    setRuleToEdit(rule);
    setIsRuleDialogOpen(true);
  };

  const hasActiveFilters =
    !!filteredParams.searchTerm ||
    !isEmpty(filteredParams.subOrgIds) ||
    !isEmpty(filteredParams.lobs) ||
    filteredParams.outcome !== 'all';
  const emptyStateText = hasActiveFilters
    ? 'No guardrails match the current filters.'
    : 'No guardrails configured yet.';

  if (isLoading || isError) {
    return <LoadingIndicator isError={isError} />;
  }

  return (
    <CardDialog noCardTitle>
      <div className="p-20">
        <Heading variant={Heading.TYPES.H3} className="pb-12">
          Guardrails
        </Heading>
        <div className="flex justify-end pb-12">
          <Button
            color="primary"
            onClick={() => setIsRuleDialogOpen(true)}
            startIcon={<PlusIcon className="text-teal-700" size={14} iconColor="currentColor" />}
          >
            Add Rule
          </Button>
        </div>
        <GuardrailRulesFilters
          organization={organization}
          subOrgIds={filteredParams.subOrgIds}
          outcome={filteredParams.outcome}
          onSearchTermChanged={(searchTerm) => setFilteredParams((p) => ({ ...p, searchTerm }))}
          onSubOrgChanged={(subOrgIds) => setFilteredParams((p) => ({ ...p, subOrgIds }))}
          onLobsChanged={(lobs) => setFilteredParams((p) => ({ ...p, lobs }))}
          onOutcomeChanged={(outcome) => setFilteredParams((p) => ({ ...p, outcome }))}
        />
        <GuardrailRulesTable
          organization={organization}
          rules={filteredRules}
          isProcessing={isProcessing}
          emptyStateText={emptyStateText}
          onEditRule={handleEditRuleClicked}
          onDeleteRule={handleDeleteRule}
          onEnabledChanged={handleEnabledChanged}
        />
      </div>
      {isRuleDialogOpen ? (
        <AddOrEditGuardrailRuleDialog
          supportedEvents={supportedEvents}
          onCancel={handleCloseRuleDialog}
          onSubmit={handleSubmitRule}
          rule={ruleToEdit}
        />
      ) : null}
    </CardDialog>
  );
};

export default Guardrails;
